import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import Flag from './Flag';

/**
 * LanguageSwitcher — pill-shaped picker in the customer header.
 *
 * Variant `a` from the design discussion: SVG flag + 2-letter code +
 * chevron. Works on a dark header (glassy white) and on a light auth
 * page (`variant="light"`). The popover lists the 7 supported
 * languages with their native names.
 *
 * Picking a language:
 *   • calls `i18n.changeLanguage()` so the UI flips on the spot
 *   • persists to localStorage via the detector
 *   • if the user is logged in, also fires `PUT /api/me/language`
 *     so the choice is restored from any device next time
 *
 * Hidden on admin pages — caller must gate this component (we do NOT
 * introspect roles here so it stays presentation-only).
 */
export default function LanguageSwitcher({ compact = false, variant = 'header' }) {
  const { i18n, t } = useTranslation('common');
  const { token } = useAuth() || {};
  const [open, setOpen] = useState(false);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)
    || SUPPORTED_LANGUAGES.find((l) => i18n.language?.startsWith(l.code))
    || SUPPORTED_LANGUAGES[0];

  // Close popover on outside click.
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (!e.target.closest('[data-lang-switcher]')) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const pickLanguage = async (code) => {
    setOpen(false);
    if (code === i18n.language) return;
    i18n.changeLanguage(code);
    // Mark the onboarding banner as resolved as soon as a language is
    // explicitly chosen via the switcher — the user already knows
    // where to switch language, no need to suggest it again.
    try { localStorage.setItem('lang_onboarding_seen', '1'); } catch (_) { /* private mode */ }
    // Best-effort backend persistence — fire and forget. If it fails
    // the local choice still applies; we'll re-sync next login.
    if (token) {
      try {
        const api = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
        await fetch(`${api}/api/me/language`, {
          method:  'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body:    JSON.stringify({ language: code }),
        });
      } catch (_) { /* offline / 401 — ignore */ }
    }
  };

  // Visual variants — `header` is the dark blue customer header,
  // `light` is auth pages / anywhere on a white background.
  const triggerCls = variant === 'header'
    ? 'border-white/25 bg-white/10 text-white hover:bg-white/20 hover:border-white/40'
    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300';
  const chevronCls = variant === 'header' ? 'text-white/70' : 'text-slate-400';
  const codeCls    = variant === 'header' ? 'text-white' : 'text-slate-700';

  return (
    <div className="relative inline-block" data-lang-switcher data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={t('language_switcher.title', { defaultValue: 'Change language' })}
        className={`inline-flex items-center gap-1.5 rounded-full border backdrop-blur-sm transition-all duration-150 ${triggerCls} ${
          compact ? 'px-2 py-1' : 'px-2.5 py-1.5'
        } ${open ? 'ring-2 ring-cyan-400/40' : ''}`}
        data-testid="language-switcher-trigger"
        aria-expanded={open}
      >
        <Flag code={current.code} className={compact ? 'w-4 h-3' : 'w-5 h-[14px]'} title={current.label} />
        <span className={`font-semibold tracking-wide uppercase text-[11px] ${codeCls}`}>
          {current.code === 'zh-CN' ? 'ZH' : current.code.toUpperCase()}
        </span>
        <ChevronDown className={`${compact ? 'hidden sm:block w-3 h-3' : 'w-3.5 h-3.5'} ${chevronCls} transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          data-testid="language-switcher-menu"
          className="absolute right-0 mt-2 z-[80] min-w-[200px] rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden ring-1 ring-black/5"
        >
          {SUPPORTED_LANGUAGES.map((lang) => {
            const active = lang.code === current.code;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => pickLanguage(lang.code)}
                data-testid={`language-option-${lang.code}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition ${
                  active
                    ? 'bg-cyan-50 text-cyan-900 font-semibold'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Flag code={lang.code} className="w-5 h-[14px]" title={lang.label} />
                <span className="flex-1">{lang.native}</span>
                {active && <Check className="w-4 h-4 text-cyan-600 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
